/* eslint-disable no-console */
import UrlParse from 'url-parse';
import BTCookies from '../../../../common/cookies';
import {SHOPIFY_CHECKOUT_ATTRIBUTES_COOKIE} from '../../../../shopify/constants';
import {getUrlParams} from '../../../../common/util';

export const HAS_OFFERS_NETWORK_TYPE = 'hasoffer';
export const HAS_OFFERS_USER_COOKIE = 'hasoffer';

export class HasOffersNetwork {
  constructor({networkUrl, hasOffersLoggingEnabled}) {
    this.networkUrl = networkUrl;
    this.hasOffersLoggingEnabled = hasOffersLoggingEnabled;
  }

  _funnelIds = null;

  get networkType() {
    return HAS_OFFERS_NETWORK_TYPE;
  }

  get checkoutFunnelId() {
    // For landers going through Shopify with post-purchase offers, the funnel is identified by the slug stored
    // in the order line item properties
    const {_bt_lander: postPurchaseLanderSlug} = window?.Shopify?.order?.lineItems
      .reverse()
      .flatMap(({properties = []} = {}) => properties)
      .reduce((acc, [key, value]) => ({...acc, [key]: value}), {}) || {};

    if (postPurchaseLanderSlug) {
      this._logWithCatchJs(
        '[HasOffers] Funnel Id obtained from Shopify order line item properties',
        {funnelId: postPurchaseLanderSlug},
      );
      return postPurchaseLanderSlug;
    }

    // For landers going through shopify, the funnel is identified by the slug stored in the checkout attributes
    const {checkout: {attributes: {bt_lander: btLanderSlug} = {} } = {} } = window.bt || {};
    if (btLanderSlug) {
      this._logWithCatchJs(
        '[HasOffers] Funnel Id obtained from window.bt.checkout.attributes',
        {funnelId: btLanderSlug},
      );
      return btLanderSlug;
    }

    // however, if tracking occurs on a post-checkout upsell page, the lander slug is stored within the checkout
    // attributes cookie
    const checkoutAttrCookie = BTCookies.getJSON(SHOPIFY_CHECKOUT_ATTRIBUTES_COOKIE);
    if (checkoutAttrCookie && checkoutAttrCookie.bt_lander) {
      this._logWithCatchJs(
        `[HasOffers] Funnel Id obtained from ${SHOPIFY_CHECKOUT_ATTRIBUTES_COOKIE} cookie`,
        {funnelId: checkoutAttrCookie.bt_lander},
      );
      return checkoutAttrCookie.bt_lander;
    }

    // otherwise, look at the url to determine funnel slug from url path component
    const urlPathname = window.location.pathname;
    if (urlPathname.startsWith('/checkout/')) {
      // possibly BT Checkout, grab funnel id from second path component
      const urlFunnelId = urlPathname.split('/')[2]; // url: [0]/checkout/[2] <== funnel id here
      this._logWithCatchJs('[HasOffers] Funnel Id obtained from url', {funnelId: urlFunnelId});
      return urlFunnelId;
    }

    return null;
  }

  get userTracking() {
    return BTCookies.getJSON(HAS_OFFERS_USER_COOKIE) || {};
  }

  get funnelIds() {
    if (this._funnelIds == null) {
      this._funnelIds = this._collectFunnelIds();
    }

    return this._funnelIds;
  }

  track({offerId} = {}) {
    try {
      // Changed code to only use window.location.href instead of window.top.location.href, because
      // window.top.location.href throws an error in sandboxed iframes (which is how trackersJS is
      // loaded in Shopify post-purchase upsell pages).  Since this is the only known location where
      // trackersJS is loaded in a sandboxed iframe, we should never be initiating tracking from the
      // post-purchase page.  Tracking should only be initiated from the sales lander page or the
      // online store.
      const {utm_offer: hasOffersOfferId, tune_transaction_id: transactionId} = getUrlParams(false);

      if (!hasOffersOfferId) {
        this._logWithCatchJs('[HasOffers] No offer id detected; Skipping offer tracking');
        return null;
      }

      return {
        key: `hasoffer_${this.funnelIds.join('_')}`,
        value: {
          type: this.networkType,
          offer: hasOffersOfferId,
          funnels: this.funnelIds,
          offerId,
          ts: Date.now(),
          transactionId,
        },
      };
    } catch (err) {
      this._logWithCatchJs('[HasOffers] Unable to get query params for Has Offers link detection', {err});
      return null; // Delish :3
    }
  }

  convert(order, items, {addon}, networkTracking) {
    if (addon === true) {
      this._logWithCatchJs(
        '[HasOffers] Skipping HasOffers conversion for add-on to existing purchase.',
        networkTracking,
      );
      return false;
    }

    const {funnels = [], offerId} = networkTracking;
    const {checkoutFunnelId} = this;

    if (order.source === 'shopify' && (
      offerId === '1000'
      || offerId === '2002'
      || /^9\d{3}$/.test(offerId)
    )) {
      this._logWithCatchJs('[HasOffers] StoreFront HasOffer Conversion Detected');
      return this._createConversion(networkTracking, order);
    }

    if (checkoutFunnelId && funnels.includes(checkoutFunnelId)) {
      this._logWithCatchJs('[HasOffers] Sales Lander HasOffer Conversion Detected', {checkoutFunnelId});
      return this._createConversion(networkTracking, order);
    }

    this._logWithCatchJs(`[HasOffers] conversion skipped, funnel does not match ${checkoutFunnelId}`, networkTracking);
    return false;
  }

  _logWithCatchJs(message, ...rest) {
    console.log(message, ...rest);

    if (this.hasOffersLoggingEnabled) {
      let cookieErr = null;
      let sessionId = window.btDataLayer?.sessionId;
      if (!sessionId) {
        try {
          sessionId = BTCookies.get(window.bt?.settings?.sessionCookieName);
        } catch (ex) {
          sessionId = 'failed-to-read-session-cookie';
          cookieErr = ex;
        }
      }

      if (window && window.catchjs && window.catchjs.log) {
        window.catchjs.log(`${message} (cpSessionId: ${sessionId || 'not-set'})`, {
          ...rest,
          ts: Date.now(),
          cookieErr,
        });
      }
    }
  }

  _createConversion(networkTracking, order) {
    const {offer, transactionId} = networkTracking;
    if (!offer) {
      this._logWithCatchJs('[HasOffers] Missing offer number for HasOffers conversion', networkTracking);
      return false;
    }

    if (transactionId) {
      this._logWithCatchJs(
        '[HasOffers] Skipping client-side conversion because a server-side transaction id was found on the offer.',
        networkTracking,
      );
      // return true to mark this offer complete and let server-side logic fire the conversion postback
      return true;
    }

    const {email: hasOffersEmail} = this.userTracking;
    const email = hasOffersEmail || order.email;
    const iframe = document.createElement('iframe');
    iframe.src = `${this.networkUrl}aff_l?offer_id=${offer}&adv_sub=${order.id}&adv_sub5=${encodeURIComponent(email)}`;
    iframe.width = 1;
    iframe.height = 1;
    document.body.appendChild(iframe);
    this._logWithCatchJs('[HasOffers] conversion sent', iframe.src);
    return true;
  }

  _collectFunnelIds() {
    const funnels = [];
    const url = new UrlParse(window.location.href); // changed from window.top.location.href -- see comment in track()
    if (url.pathname.startsWith('/pages/')) {
      const pageSlug = url.pathname.split('/').filter(p => p.trim()).pop();
      funnels.push(pageSlug); // BT Checkout Funnel slug
    }
    this._logWithCatchJs('[HasOffers] detected these funnels', funnels);
    return funnels;
  }
}
